/**
 * Albanian portal copy, the source every other language mirrors.
 * Strings use double quotes, so apostrophes in any language never break the file.
 * `{name}` placeholders are filled with `fill()` from `@/i18n/config`.
 */
export const sq = {
    meta: {
        lang: "sq",
        dir: "ltr" as "ltr" | "rtl",
        siteTagline: "Botime, përkthime dhe kurse",
        siteDescription:
            "Instituti Germa është institucion kulturor e edukativo-arsimor, që vepron në fushën e botimeve, përkthimeve dhe kurseve të Kuranit e të gjuhëve të huaja."
    },

    /** Names of the portal languages, written in this language. */
    languageNames: {
        sq: "Shqip",
        en: "Anglisht",
        de: "Gjermanisht",
        es: "Spanjisht",
        tr: "Turqisht",
        bs: "Boshnjakisht",
        ar: "Arabisht"
    },

    common: {
        home: "Ballina",
        homeAria: "Instituti Germa, ballina",
        breadcrumb: "Rruga e faqes",
        mainNav: "Kryesore",
        menu: "Menu",
        openMenu: "Hap menunë",
        closeMenu: "Mbyll menunë",
        contactUs: "Na kontaktoni",
        language: "Gjuha",
        chooseLanguage: "Zgjidhni gjuhën e portalit",
        instituteName: "Instituti Germa"
    },

    nav: {
        libra: "Libra",
        kurani: "Kurani",
        videoleksione: "Videoleksione",
        kurse: "Kurse",
        audiolibra: "Audiolibra",
        rrethNesh: "Për ne",
        kontakt: "Kontakt"
    },

    footer: {
        tagline:
            "Botime dhe përkthime të literaturës islame, kurse për leximin e Kuranit dhe mësimin e gjuhëve të huaja.",
        links: "Ndërlidhje",
        books: "Libra",
        allBooks: "Të gjithë librat",
        contact: "Kontakt",
        contactNote:
            "Na shkruani për të porositur libra, për t’u regjistruar në kurse ose për të propozuar bashkëpunim.",
        rights: "Të gjitha të drejtat të rezervuara."
    },

    verse: {
        text: "Thuaj: “Zoti im, shtoma dijen!”",
        source: "Kurani, 20:114"
    },

    home: {
        seoDescription:
            "Libra për shkarkim falas, videoleksione, audiolibra dhe kurse për leximin e Kuranit dhe mësimin e gjuhëve.",
        hero: {
            eyebrow: "Mirë se vini",
            intro:
                "Instituti Germa është institucion kulturor dhe edukativo-arsimor. Botojmë libra fetarë, gjuhësorë e edukativë, përgatisim videoleksione dhe organizojmë kurse për leximin e Kuranit dhe mësimin e gjuhëve të huaja.",
            primaryCta: "Shfleto botimet",
            secondaryCta: "Regjistrohu në kurse",
            pillars: ["Libra", "Videoleksione", "Kurse", "Audiolibra"],
            cue: "Zbulo më shumë",
            category: "Botim",
            viewBook: "Shiko librin",
            previous: "Libri i mëparshëm",
            next: "Libri tjetër",
            goTo: "Shko te libri {number}"
        },
        marquee: {
            rowOne: ["Botime", "Përkthime", "Kurse të Kuranit", "Videoleksione", "Audiolibra", "Gjuhë të huaja"],
            rowTwo: ["Abetarja Kuranore", "Texhvid", "Dije", "Edukim", "Trashëgimi", "Metodë bashkëkohore"]
        },
        about: {
            eyebrow: "Rreth institutit",
            title: "Në shërbim të dijes dhe edukimit",
            intro:
                "Puna jonë synon t’i ndihmojë njerëzit të mësojnë, të njohin më mirë fenë dhe të zhvillojnë njohuritë gjuhësore. Për këtë, kujdesemi që librat dhe materialet mësimore të kenë përmbajtje të saktë e shpjegime të qarta, ndërsa kurset t’u përshtaten njohurive dhe nevojave të nxënësve.",
            features: [
                "Botime dhe përkthime të literaturës fetare, gjuhësore dhe edukative.",
                "Kurse për leximin e Kuranit dhe mësimin e gjuhëve të huaja, online dhe në klasë.",
                "Videoleksione dhe audiolibra që mund t’i ndiqni në internet."
            ]
        },
        stats: {
            titles: "Tituj të botuar",
            languages: "Gjuhë të botimeve",
            fields: "Fusha veprimtarie",
            online: "Qasje online"
        },
        services: {
            eyebrow: "Çfarë ofrojmë",
            title: "Veprimtaria jonë",
            more: "Mëso më shumë",
            items: {
                libra: {
                    title: "Libra",
                    description:
                        "Libra fetarë, gjuhësorë dhe edukativë, të përgatitur ose të përkthyer nga Instituti Germa.",
                    cta: "Shih librat"
                },
                videoleksione: {
                    title: "Videoleksione",
                    description: "Mësime në video për leximin e Kuranit dhe tema të tjera fetare e edukative.",
                    cta: "Ndiq videoleksionet"
                },
                kurse: {
                    title: "Kurse",
                    description:
                        "Kurse për leximin e Kuranit dhe mësimin e gjuhëve të huaja, në klasë dhe online.",
                    cta: "Shih kurset"
                },
                audiolibra: {
                    title: "Audiolibra",
                    description: "Botime të lexuara në format audio, për ata që dëshirojnë t’i dëgjojnë.",
                    cta: "Dëgjo audiolibrat"
                }
            }
        },
        /** Book rows: the first one shows books in the portal language. */
        booksLocal: {
            title: "Libra në gjuhën shqipe",
            description: "Libra nga autorë shqiptarë dhe të huaj, të disponueshëm për shkarkim falas."
        },
        booksOther: {
            title: "Libra në gjuhë të huaja",
            description: "Këtu gjeni botimet tona në disa gjuhë, të cilat mund t’i shkarkoni falas."
        },
        booksCarouselTitle: "Libra që lënë gjurmë",
        booksCarouselDescription:
            "Vepra fetare, edukative dhe kulturore, të përgatitura me kujdes për lexuesin.",
        seeAllBooks: "Shih të gjithë librat",
        mostRead: {
            eyebrow: "Sipas lexuesve",
            title: "Më të lexuarat",
            description: "Librat që lexuesit tanë i hapin dhe i shkarkojnë më shpesh."
        },
        kurani: {
            badge: "Projekt i veçantë",
            title: "Kurani fisnik",
            subtitle: "Përkthim i kuptimeve në gjuhën shqipe",
            text: "Lexoni Kuranin së bashku me përkthimin e kuptimeve të tij në gjuhën shqipe nga Jusuf H. Kastrati. Përkthimi është i qasshëm falas në Kurani.info.",
            cta: "Rreth përkthimit",
            readOn: "Lexoni në Kurani.info",
            logoAlt: "Logoja e platformës Kurani.info",
            highlights: [
                {
                    title: "Saktësi dhe besnikëri",
                    text: "Përcjellje e kujdesshme e kuptimeve të ajeteve, duke u mbështetur në tefsire të besueshme."
                },
                {
                    title: "Shqipe e qartë dhe e rrjedhshme",
                    text: "Shqipe e natyrshme, me përpjekje për të ruajtur, brenda mundësive të saj, veçoritë e shprehjes kuranore."
                },
                {
                    title: "Fusnota sqaruese",
                    text: "Shpjegime të shkurtra që ndihmojnë në kuptimin e ajeteve aty ku është e nevojshme."
                }
            ]
        }
    },

    cta: {
        eyebrow: "Bashkohu me ne",
        title: "Për regjistrim në kurse, porosi të botimeve ose bashkëpunim, na kontaktoni.",
        primary: "Shiko kurset",
        secondary: "Na kontaktoni"
    },

    books: {
        seoTitle: "Libra islamë",
        seoDescription:
            "Katalogu i botimeve të Institutit Germa: libra fetarë, edukativë dhe kulturorë në shqip, gjermanisht e gjuhë të tjera, me përkthim dhe redaktim profesional.",
        listName: "Botimet e Institutit Germa",
        title: "Botimet tona",
        description:
            "Këtu gjeni botimet tona në shqip dhe në gjuhë të huaja, të cilat mund t’i shkarkoni falas.",
        crumb: "Libra",
        searchLabel: "Kërko libra",
        searchPlaceholder: "Kërko sipas titullit ose autorit",
        authorChip: "Autori: {name}",
        clearAuthor: "Hiq filtrin e autorit",
        clearSearch: "Pastro kërkimin",
        categoryLabel: "Kategoria",
        allCategories: "Të gjitha kategoritë",
        languageLabel: "Gjuha",
        allLanguages: "Të gjitha gjuhët",
        readingList: "Lista ime e leximit",
        readingListHintBefore: "Shtypni shenjën",
        readingListHintAfter: "te një libër për ta ruajtur këtu.",
        readingListEmpty:
            "Lista juaj e leximit është ende bosh. Shtypni shenjën e ruajtjes te çdo libër që dëshironi ta lexoni më vonë.",
        foundOne: "U gjet 1 libër",
        foundMany: "U gjetën {count} libra",
        noResults: "Nuk u gjet asnjë libër me këto kritere.",
        showAll: "Shfaqi të gjithë librat",
        groupLocal: "Në gjuhën shqipe",
        groupOther: "Në gjuhë të tjera",
        mostRead: "Më i lexuar",
        publishedBy: "Botim i Institutit Germa",
        viewBook: "Shiko librin {title}",
        coverAlt: "Kopertina e librit {title}",
        save: "Ruaje në listë",
        saved: "Në listën time",
        remove: "Hiqe nga lista"
    },

    /** Labels on a book page. On the Albanian portal they follow the book's own language. */
    bookPage: {
        lang: "sq",
        dir: "ltr" as "ltr" | "rtl",
        about: "Rreth librit",
        highlights: "Pikat kryesore",
        details: "Detajet",
        language: "Gjuha",
        languageName: "Shqip",
        category: "Kategoria",
        author: "Autor",
        authorLine: "Autor: {author}.",
        publishedBy: "Botim i Institutit Germa.",
        fallbackDescription: "{title}. Libër në gjuhën {language}.",
        publisher: "Botues",
        cta: "Të interesuar për këtë libër? Na shkruani për porosi apo informacione të mëtejshme.",
        contact: "Na kontaktoni",
        home: "Ballina",
        books: "Libra",
        book: "Libër",
        breadcrumb: "Rruga e faqes",
        coverAlt: "Kopertina e librit {title}",
        readOnline: "Lexo online",
        orderOrAsk: "Porosit ose pyet",
        allBooks: "Të gjithë librat",
        readerTitle: "Lexo librin online",
        readerText: "Shfletoni të gjithë librin drejtpërdrejt këtu, falas.",
        openNewTab: "Hap në skedë të re",
        relatedEyebrow: "Vazhdoni leximin",
        moreByAuthor: "Libra të tjerë nga ky autor",
        allByAuthor: "Të gjithë librat e autorit",
        authorLinkTitle: "Shih librat e këtij autori",
        relatedTitle: "Mund t’ju interesojë edhe",
        seeAllBooks: "Shih të gjithë librat",
        otherEditions: "Ky libër në gjuhë të tjera",
        watchVideos: "Shiko videoleksionet",
        downloadPdf: "Shkarko PDF",
        continueFrom: "Vazhdo nga faqja {page}",
        startOver: "Lexo nga fillimi",
        page: "Faqja",
        pageOf: "nga",
        prevPage: "Faqja e mëparshme",
        nextPage: "Faqja tjetër",
        zoomIn: "Zmadho",
        zoomOut: "Zvogëlo",
        fullscreen: "Ekran i plotë",
        closeReader: "Mbyll lexuesin",
        loadingBook: "Libri po hapet…",
        readerError: "Libri nuk u hap dot këtu. Provoni ta hapni në skedë të re.",
        saveBook: "Ruaje në listë",
        savedBook: "Në listën time",
        share: "Shpërndaje",
        copyLink: "Kopjo lidhjen",
        linkCopied: "Lidhja u kopjua",
        email: "Email",
        moreOptions: "Opsione të tjera",
        displaySettings: "Pamja e leximit",
        themeLight: "Ditë",
        themeSepia: "Sepia",
        themeNight: "Natë",
        pageSize: "Madhësia e tekstit",
        reviews: {
            title: "Vlerësimet e lexuesve",
            intro: "Si ju duket ky libër? Lini një vlerësim me yje dhe, nëse dëshironi, disa fjalë për lexuesit e tjerë.",
            average: "{average} nga 5",
            count: "{count} vlerësime",
            countOne: "1 vlerësim",
            empty: "Ky libër nuk ka ende vlerësime. Bëhuni i pari që e vlerëson.",
            formTitle: "Vlerësojeni librin",
            ratingLabel: "Vlerësimi juaj",
            star: "{count} yje",
            starOne: "1 yll",
            name: "Emri juaj",
            namePlaceholder: "p.sh. Arta",
            comment: "Komenti (nuk është i detyrueshëm)",
            commentPlaceholder: "Çfarë ju pëlqeu te ky libër?",
            submit: "Dërgo vlerësimin",
            sending: "Duke dërguar…",
            success: "Faleminderit! Vlerësimi juaj do të shfaqet pasi ta shqyrtojë Instituti.",
            errorRating: "Ju lutemi zgjidhni sa yje i jepni librit.",
            errorName: "Ju lutemi shkruani emrin tuaj (të paktën dy shkronja).",
            errorThrottled: "Sot keni dërguar shumë vlerësime. Provoni sërish nesër.",
            error: "Vlerësimi nuk u dërgua. Provoni sërish pas pak.",
            moderated: "Vlerësimet shfaqen pasi t’i shqyrtojë Instituti."
        },
        quote: {
            button: "Shpërndaj një citat",
            title: "Shpërndaje një citat",
            intro: "Zgjidhni me maus ose me gisht një fjali në faqen e librit, ose shkruajeni vetë këtu. Ne e kthejmë në një foto për Instagram ose WhatsApp.",
            textLabel: "Citati",
            placeholder: "Shkruani ose ngjitni fjalinë që ju preku…",
            formatLabel: "Forma e fotos",
            square: "Katror (postim)",
            story: "Vertikal (story)",
            share: "Shpërndaje foton",
            download: "Shkarko foton",
            copyText: "Kopjo tekstin",
            copied: "Teksti u kopjua",
            close: "Mbyll",
            tooLong: "Citati është shumë i gjatë. Shkurtojeni pak (deri në {max} shkronja).",
            empty: "Shkruani një citat që të shfaqet në foto.",
            fromSelection: "Shpërndaje si citat",
            preview: "Pamja paraprake e fotos",
            shareText: "Nga libri „{title}”"
        }
    },

    share: {
        share: "Shpërndaje",
        copyLink: "Kopjo lidhjen",
        linkCopied: "Lidhja u kopjua",
        email: "Email",
        moreOptions: "Opsione të tjera"
    },

    audiobooks: {
        seoTitle: "Audiolibra në gjuhën shqipe",
        seoDescription:
            "Audiolibra fetarë dhe edukativë në shqip, të lexuar qartë e profesionalisht. Dëgjoji drejtpërdrejt në faqe, në çdo kohë dhe nga çdo pajisje.",
        listName: "Audiolibra të Institutit Germa",
        title: "Audiolibra në gjuhën shqipe",
        description:
            "Këtu gjeni botimet tona në format audio. Mund t’i dëgjoni drejtpërdrejt në këtë faqe ose t’i shkarkoni falas, pa regjistrim.",
        crumb: "Audiolibra",
        comingSoon: "Së shpejti",
        comingSoonText: "Audiolibrat tanë janë në përgatitje. Ndiqni kanalet tona për t’i dëgjuar të parët.",
        badge: "Audiolibër",
        author: "Autor:",
        openBook: "Hap librin",
        resumeFrom: "Vazhdo nga {time}",
        back15: "Kthehu 15 sekonda",
        forward15: "Përpara 15 sekonda",
        play: "Luaj",
        pause: "Ndalo",
        position: "Pozicioni i dëgjimit",
        speedGroup: "Shpejtësia e dëgjimit",
        speed: "Shpejtësia",
        speedValue: "Shpejtësia {value}",
        download: "Shkarko audiolibrin",
        youtube: "Dëgjoje në YouTube",
        miniLabel: "Po dëgjoni",
        closePlayer: "Mbyll dëgjuesin",
        openAudiobook: "Shko te audiolibri",
        coverAlt: "Kopertina e audiolibrit {title}",
        otherAudiobooks: "Audiolibra të tjerë",
        allAudiobooks: "Të gjithë audiolibrat",
        openPage: "Hap faqen e audiolibrit",
        pageSeoDescription: "Dëgjoni falas audiolibrin “{title}” në gjuhën shqipe, drejtpërdrejt në faqe, ose shkarkojeni pa regjistrim."
    },

    videos: {
        seoTitle: "Videoleksione për të mësuar Kuranin",
        seoDescription:
            "{count} videoleksione falas për të mësuar shkronjat e Kuranit, në shqip, gjermanisht dhe anglisht. Nga shkronja e parë deri te leximi i rrjedhshëm.",
        listName: "Videoleksionet e Institutit Germa",
        title: "Mësoni të lexoni Kuranin",
        description:
            "Këto videoleksione ju ndihmojnë të mësoni shkronjat arabe dhe leximin e Kuranit, hap pas hapi. Mësimet janë në shqip, gjermanisht dhe anglisht dhe mund t’i ndiqni falas në këtë faqe, pa regjistrim.",
        crumb: "Videoleksione",
        chooseLanguage: "Zgjidh gjuhën:",
        player: {
            videos: "video",
            youtube: "Ndiqeni në YouTube",
            list: "Lista e mësimeve",
            promo: "Video prezantuese",
            promoShort: "Promo",
            lesson: "Mësimi",
            of: "nga",
            playing: "Duke u shfaqur",
            shareLesson: "Shpërndaje mësimin",
            openBook: "Hap librin",
            markDone: "Shëno si të mësuar",
            done: "U mësua",
            progress: "mësuar",
            resume: "Vazhdo te",
            lessonsDone: "{done} nga {total} mësime të mësuara",
            progressLabel: "Përparimi në këtë seri",
            seriesDone: "E keni përfunduar këtë seri. Të lumtë!",
            progressHint: "Shënoni mësimet që i keni mësuar dhe ndiqni përparimin tuaj.",
            prevLesson: "Mësimi i mëparshëm",
            nextLesson: "Mësimi tjetër",
            allLessons: "Të gjitha mësimet e serisë",
            watchOnYoutube: "Shikoje në YouTube",
            aboutLesson: "Rreth mësimit",
            backToSeries: "Kthehu te seria",
            openLessonPage: "Hap faqen e mësimit",
            lessonIntro: "Mësimi {number} nga {total} i serisë “{series}”.",
            promoIntro: "Video prezantuese e serisë “{series}”.",
            freeLesson: "Videoleksion falas i Institutit Germa, pa regjistrim."
        }
    },

    courses: {
        seoTitle: "Kurse të Kuranit dhe gjuhëve",
        seoDescription:
            "Kurse për mësimin e Kuranit nga shkronjat e para deri te leximi me texhvid, si dhe kurse gjuhe. Online dhe në klasë, me regjistrim të hapur.",
        listName: "Kurset e Institutit Germa",
        title: "Kurse për leximin e Kuranit dhe mësimin e gjuhëve",
        description:
            "Organizojmë kurse për fillestarë dhe për ata që dëshirojnë të përmirësojnë njohuritë e tyre. Mësimet zhvillohen online ose në klasë, sipas kursit dhe grupit.",
        crumb: "Kurse",
        posterAlt: "Poster i kursit {title}",
        teacher: "Mësuesja:",
        details: "Shihni hollësitë e kursit",
        listTitle: "Kurset tona",
        listDescription:
            "Organizojmë kurse për mësimin e leximit të Kuranit dhe të texhvidit. Planifikojmë të rifillojmë edhe kursin e gjuhës arabe. Datat, oraret dhe mënyra e regjistrimit njoftohen për çdo grup të ri.",
        registerTitle: "Dëshironi të regjistroheni në një kurs?",
        registerDescription:
            "Plotësoni formularin për të shprehur interesimin tuaj. Sapo të hapet një grup i ri për kursin që keni zgjedhur, do t’ju kontaktojmë për t’ju njoftuar rreth datës së fillimit, orarit dhe mënyrës së zhvillimit të mësimeve.",
        plannedSuffix: "planifikohet rifillimi",
        contactFallback: "Na kontaktoni",
        items: {
            "shkronjat-e-kuranit": {
                title: "Mësimi i leximit të Kuranit",
                description:
                    "Kurs për fillestare, sipas librit Abetarja kuranore. Mësimi nis me njohjen dhe shqiptimin e shkronjave arabe, vijon me lidhjen e tyre në fjalë dhe ushtrime për leximin e Kuranit.",
                level: "Për fillestare",
                format: "Online",
                badge: "Për fillestarë",
                summary:
                    "Kurs sipas librit Abetarja kuranore, që nis me njohjen dhe shqiptimin e shkronjave arabe dhe vijon me lidhjen e tyre në fjalë e ushtrime leximi.",
                cta: "Na kontaktoni për grupet e reja"
            },
            "leximi-me-texhvid": {
                title: "Mësimi i texhvidit",
                description:
                    "Mësimi dhe zbatimi i rregullave të texhvidit, me ushtrime praktike dhe korrigjim të leximit.",
                level: "Për ata që dinë të lexojnë Kuranin",
                format: "Online",
                badge: "",
                summary: "",
                cta: "Na kontaktoni për grupet e reja"
            },
            "gjuha-arabe": {
                title: "Gjuha arabe",
                description:
                    "Kurs për mësimin e leximit, shkrimit dhe të kuptuarit të gjuhës arabe. Hollësitë do të publikohen kur të përcaktohet rifillimi i kursit.",
                level: "Planifikohet rifillimi",
                format: "Online",
                badge: "",
                summary: "",
                cta: "Na kontaktoni për më shumë informacion"
            }
        }
    },

    courseForm: {
        name: "Emri dhe mbiemri",
        phone: "Numri i telefonit",
        email: "Adresa e emailit",
        course: "Kursi që ju intereson",
        message: "Mesazh",
        optional: "(opsional)",
        messagePlaceholder: "Shënoni pyetjet ose kërkesat tuaja.",
        notice: "Plotësimi i formularit nuk përbën regjistrim përfundimtar në kurs.",
        submit: "Dërgoni kërkesën",
        submitting: "Duke dërguar…",
        successTitle: "Kërkesa u dërgua",
        successText:
            "Faleminderit për interesimin. Do t’ju kontaktojmë kur të hapet një grup i ri për kursin që keni zgjedhur.",
        again: "Dërgoni një kërkesë tjetër",
        errors: {
            name: "Ju lutemi shkruani emrin tuaj.",
            phone: "Ju lutemi shkruani një numër telefoni të vlefshëm.",
            email: "Ju lutemi shkruani një email të vlefshëm.",
            course: "Ju lutemi zgjidhni një kurs.",
            generic: "Kërkesa nuk u dërgua. Ju lutemi provoni sërish."
        }
    },

    contact: {
        seoTitle: "Kontakt",
        seoDescription:
            "Na shkruani për porosi librash, regjistrime në kurset e Kuranit dhe të gjuhëve, ose për bashkëpunime me Institutin Germa. Përgjigjemi sa më shpejt.",
        title: "Na kontaktoni",
        description:
            "Na shkruani për porosi librash, regjistrim në kurse, bashkëpunim ose pyetje rreth veprimtarisë sonë.",
        crumb: "Kontakt",
        howTitle: "Si të na kontaktoni?",
        howText: "Mund të na shkruani me email ose përmes formularit.",
        email: "Email",
        response: "Përgjigjja",
        responseValue: "Brenda 3 ditësh",
        follow: "Na ndiqni në rrjetet sociale"
    },

    contactForm: {
        name: "Emri dhe mbiemri",
        namePlaceholder: "Emri dhe mbiemri",
        email: "Adresa e emailit",
        emailPlaceholder: "ju@example.com",
        subject: "Tema",
        optional: "(opsionale)",
        subjectPlaceholder: "Për çfarë po na shkruani?",
        message: "Mesazhi",
        messagePlaceholder: "Shkruani mesazhin tuaj këtu.",
        submit: "Dërgoni mesazhin",
        submitting: "Duke dërguar…",
        successTitle: "Faleminderit!",
        successText: "Mesazhi juaj u dërgua me sukses. Do t’ju përgjigjemi sa më shpejt që të jetë e mundur.",
        again: "Dërgo një mesazh tjetër",
        errors: {
            name: "Ju lutemi shkruani emrin tuaj.",
            email: "Ju lutemi shkruani një email të vlefshëm.",
            message: "Ju lutemi shkruani një mesazh më të gjatë.",
            generic: "Mesazhi nuk u dërgua. Ju lutemi provoni sërish."
        }
    },

    about: {
        seoTitle: "Për ne",
        seoDescription:
            "Instituti Germa është institucion kulturor e edukativo-arsimor i themeluar nga hoxhë Jusuf Kastrati, për botime, përkthime dhe kurse të Kuranit e gjuhëve.",
        title: "Instituti Germa",
        description: "Në shërbim të dijes dhe edukimit, përmes librave dhe mësimdhënies.",
        crumb: "Për ne",
        missionTitle: "Misioni ynë",
        mission: [
            "Instituti Germa është institucion kulturor dhe edukativo-arsimor që vepron në fushën e botimeve, përkthimeve dhe mësimdhënies. Botojmë literaturë fetare, gjuhësore dhe edukative, organizojmë kurse për leximin e Kuranit dhe mësimin e gjuhëve, si dhe përgatisim videoleksione e materiale mësimore.",
            "Në përgatitjen e botimeve, i kushtojmë kujdes saktësisë së përmbajtjes, cilësisë së përkthimit dhe redaktimit gjuhësor. Në kurse, ndërthurim shpjegimin me ushtrime praktike, sipas nivelit dhe nevojave të nxënësve."
        ],
        valuesTitle: "Parimet e punës sonë",
        values: [
            {
                title: "Saktësia",
                text: "Mbështetemi në burime të besueshme dhe e rishikojmë përmbajtjen para botimit, me kujdes për saktësinë fetare dhe gjuhësore."
            },
            {
                title: "Qartësia",
                text: "Përdorim gjuhë të rrjedhshme dhe shpjegime të kuptueshme, duke i sqaruar termat dhe çështjet që kërkojnë njohuri paraprake."
            },
            {
                title: "Kujdesi për nxënësin",
                text: "Mësimet i përshtatim sipas moshës dhe njohurive të nxënësve. U japim hapësirë pyetjeve dhe i ndihmojmë të përparojnë hap pas hapi."
            },
            {
                title: "Përgjegjësia",
                text: "Mirëpresim vërejtjet dhe sugjerimet dhe i korrigjojmë gabimet që vërehen në botimet dhe materialet tona."
            }
        ],
        leadership: "Udhëheqja",
        founderShort: "Themelues & drejtor",
        portraitAlt: "Portret i {name}",
        directorRole: "Themelues dhe drejtor i Institutit Germa",
        directorBio: [
            "Instituti Germa udhëhiqet nga hoxhë Jusuf Kastrati, themelues dhe drejtor i Institutit. Ai ka përfunduar studimet universitare në Fakultetin e Sheriatit të Universitetit Islam „Imam Muhamed ibn Saud“ në Rijad, në degën e jurisprudencës islame dhe bazave të saj. Në të njëjtin universitet ka përfunduar edhe studimet master në Kulturë Islame, ndërsa aktualisht vijon studimet e doktoratës në degën e Qytetërimit Islam në Universitetin Zejtuna në Tunizi.",
            "Hoxhë Jusufi ka përvojë shumëvjeçare në përkthimin e literaturës islame dhe në mësimdhënie. Ka përkthyer libra fetarë dhe ka mbajtur kurse për mësimin e leximit të Kuranit dhe të gjuhës arabe, me shpjegime në shqip dhe gjermanisht. Një pjesë të veçantë të punës së tij ia ka kushtuar edukimit fetar të të rinjve."
        ]
    },

    kurani: {
        seoTitle: "Kurani fisnik me përkthim të kuptimeve në shqip",
        seoDescription:
            "Lexoni tekstin arab të Kuranit së bashku me përkthimin e kuptimeve në shqip nga Jusuf Kastrati, falas në Kurani.info.",
        title: "Kurani fisnik",
        subtitle: "Përkthim i kuptimeve në gjuhën shqipe",
        description:
            "Lexoni Kuranin së bashku me përkthimin e kuptimeve të tij në gjuhën shqipe nga Jusuf H. Kastrati. Përkthimi është i qasshëm falas në Kurani.info.",
        crumb: "Kurani",
        readOn: "Lexoni në Kurani.info",
        logoAlt: "Logoja e platformës Kurani.info",
        aboutTitle: "Rreth përkthimit",
        about: [
            "Ky përkthim është përgatitur për t’i përcjellë lexuesit shqiptar kuptimet e ajeteve me saktësi dhe në një shqipe të qartë e të rrjedhshme. Puna është mbështetur në tefsire të besueshme dhe ka kaluar nëpër rishikime të shumta fetare e gjuhësore.",
            "Çdo përkthim është një përpjekje njerëzore për të përcjellë kuptimet e ajeteve dhe nuk mund ta pasqyrojë plotësisht pasurinë gjuhësore e kuptimore të origjinalit. Prandaj, ai shërben si ndihmë për ta kuptuar Kuranin, ndërsa vetë Kurani është Libri i shpallur nga Allahu në gjuhën arabe."
        ],
        featuresTitle: "Veçoritë e përkthimit",
        features: [
            {
                title: "Saktësi dhe besnikëri",
                text: "Kujdes në përcjelljen e kuptimeve të ajeteve, duke u mbështetur në tefsire të besueshme."
            },
            {
                title: "Shqipe e qartë dhe e rrjedhshme",
                text: "Shqipe e qartë dhe e rrjedhshme, me përpjekje për të ruajtur, brenda mundësive të saj, veçoritë e shprehjes kuranore."
            },
            {
                title: "Njëtrajtshmëri në stil dhe terminologji",
                text: "Përdorim i njëtrajtshëm i terminologjisë dhe ruajtje e termave islamë të njohur në gjuhën shqipe."
            },
            {
                title: "Fusnota të shkurtra",
                text: "Sqarime që e ndihmojnë lexuesin të kuptojë ajetet aty ku nevojitet shpjegim shtesë."
            },
            {
                title: "Rishikim fetar dhe gjuhësor",
                text: "Shqyrtime të përsëritura nga specialistë të fushave përkatëse, për saktësinë e kuptimeve dhe të shprehjes në shqip."
            },
            {
                title: "Qasje e lirë",
                text: "Përkthimi mund të lexohet falas në internet. Versioni elektronik mundëson përditësimin e tij me korrigjime dhe përmirësime të mëtejshme."
            }
        ],
        translatorBadge: "Përkthyesi",
        translatorRole: "Doktorant në Qytetërimin Islam",
        translatorText:
            "Përkthimi i kuptimeve të Kuranit fisnik në gjuhën shqipe është fryt i një pune shumëvjeçare kërkimore, përkthimore dhe redaktuese.",
        teamTitle: "Ekipi i redaktimit dhe shqyrtimit",
        roles: {
            religious: "Redaktim fetar",
            consulting: "Konsulencë fetare",
            language: "Redaktim gjuhësor"
        },
        noteTitle: "Shënim",
        note: "Ky përkthim ndodhet në fazat përfundimtare të redaktimit dhe shqyrtimit shkencor. Mirëpresim çdo vërejtje dhe sugjerim që mund të ndihmojë në përcjelljen sa më të saktë të kuptimeve të Kuranit fisnik.",
        writeUs: "Na shkruani",
        ctaTitle: "Lexoni përkthimin",
        ctaText: "Tekstin arab, përkthimin në shqip dhe fusnotat sqaruese i gjeni në Kurani.info.",
        ctaButton: "Hapni Kurani.info"
    },

    search: {
        open: "Kërko",
        title: "Kërko në portal",
        placeholder: "Kërko libra, audiolibra, videoleksione, kurse…",
        close: "Mbyll kërkimin",
        hint: "Shkruani të paktën dy shkronja për të kërkuar.",
        empty: "Nuk u gjet asgjë për “{query}”.",
        error: "Kërkimi nuk funksionoi. Provoni sërish pas pak.",
        loading: "Duke kërkuar…",
        groups: {
            books: "Libra",
            audiobooks: "Audiolibra",
            videos: "Videoleksione",
            courses: "Kurse"
        }
    },

    offline: {
        title: "Jeni pa internet",
        text: "Kjo faqe nuk është ruajtur ende në këtë pajisje. Kur të lidheni sërish me internetin, provoni përsëri. Ndërkohë mund të lexoni librat që keni hapur më parë.",
        retry: "Provo sërish",
        home: "Faqja kryesore",
        savedTitle: "Libra të ruajtur për lexim pa internet",
        savedEmpty: "Ende nuk keni libra të ruajtur. Çdo libër që e hapni në lexuesin e faqes ruhet vetë në këtë pajisje."
    },

    pwa: {
        install: "Instalo aplikacionin"
    },

    notFound: {
        title: "Faqja nuk u gjet",
        text: "Ndoshta faqja është zhvendosur ose nuk ekziston më. Kthehuni në ballinë për të vazhduar udhëtimin tuaj me dijen.",
        back: "Kthehu në ballinë"
    },

    serverError: {
        title: "Diçka shkoi keq",
        text: "Ndodhi një gabim i papritur nga ana jonë. Ju lutemi provoni sërish pas pak.",
        back: "Kthehu në ballinë"
    }
}

export type Messages = typeof sq
